<!--
  ~ Copyright (C) 2026 - Potentially Ltd
  ~
  ~ Please see distribution for license.
  -->

@if (!invitationId) {
  <button
    type="button"
    class="v3-back-button"
    [attr.aria-label]="'translations.global.ariaLabels.backToSignIn' | transloco"
    (click)="onBackClicked()"
  >
    <ptl-icon [icon]="LucideArrowLeft" size="xs"></ptl-icon>
    <span>{{ 'translations.public.title.registration' | transloco }}</span>
  </button>
}

<div
  class="v3-signup-form"
  [formGroup]="signupDetailsFormGroup"
  role="form"
  [attr.aria-label]="'translations.global.ariaLabels.signUpForm' | transloco"
>
  @if (!invitationId && organizationData?.whitelist.length) {
    <div
      class="v3-whitelist-info fs-body-xs-alt"
      tabindex="0"
      [attr.aria-label]="'translations.whitelist.description.registerPermittedEmailsTitle' | transloco"
      (click)="openWhitelistDomains($event)"
      (keydown.enter)="openWhitelistDomains($event)"
      [innerHTML]="'translations.whitelist.description.registerPermittedEmailsTitle' | transloco | safeHtml"
    ></div>
  }

  <ptl-sg-input
    class="v3-field"
    type="text"
    formControlName="firstName"
    [label]="'translations.global.input.label.firstName' | transloco"
    [ariaLabel]="'translations.global.ariaLabels.firstNameField' | transloco"
  ></ptl-sg-input>
  @if (signupDetailsFormGroup.controls.firstName.touched && signupDetailsFormGroup.controls.firstName.hasError('required')) {
    <p class="v3-field-error fs-body-xs-alt">{{ 'translations.global.input.error.enterValidFirstName' | transloco }}</p>
  }

  <ptl-sg-input
    class="v3-field"
    type="text"
    formControlName="lastName"
    [label]="'translations.global.input.label.lastName' | transloco"
    [ariaLabel]="'translations.global.ariaLabels.lastNameField' | transloco"
  ></ptl-sg-input>
  @if (signupDetailsFormGroup.controls.lastName.touched && signupDetailsFormGroup.controls.lastName.hasError('required')) {
    <p class="v3-field-error fs-body-xs-alt">{{ 'translations.global.input.error.enterValidLastName' | transloco }}</p>
  }

  @if (hasAffiliation) {
    <ptl-select
      class="v3-field"
      formControlName="affiliation"
      [options]="affiliationOptions"
      [label]="'translations.global.input.label.affiliation' | transloco"
    ></ptl-select>
    @if (signupDetailsFormGroup.controls.affiliation?.touched && signupDetailsFormGroup.controls.affiliation?.hasError('required')) {
      <p class="v3-field-error fs-body-xs-alt">{{ 'translations.global.input.error.selectAffiliation' | transloco }}</p>
    }
  }

  <ptl-sg-input
    class="v3-field"
    type="email"
    formControlName="email"
    [label]="'translations.global.input.label.email' | transloco"
  ></ptl-sg-input>
  @if (signupDetailsFormGroup.controls.email.touched && signupDetailsFormGroup.controls.email.hasError('required')) {
    <p class="v3-field-error fs-body-xs-alt">{{ 'translations.global.input.error.enterEmail' | transloco }}</p>
  }

  <div class="v3-password-field">
    <ptl-sg-input
      class="v3-field"
      [type]="showPasswordSelected ? 'text' : 'password'"
      formControlName="password"
      autocomplete="off"
      [label]="'translations.global.input.label.password' | transloco"
      [description]="passwordFocusState ? ('translations.global.input.error.enterValidPassword' | transloco) : ''"
      (focustEnterAction)="passwordFocusState = true"
      (focusoutAction)="passwordFocusState = false"
    ></ptl-sg-input>
    <button
      type="button"
      class="v3-password-toggle"
      [attr.aria-label]="'translations.global.ariaLabels.showOrHidePassword' | transloco"
      (click)="showPasswordSelected = !showPasswordSelected"
    >
      <ptl-icon [icon]="showPasswordSelected ? LucideEyeOff : LucideEye" size="xs"></ptl-icon>
    </button>
  </div>
  @if (signupDetailsFormGroup.controls.password.touched && signupDetailsFormGroup.controls.password.hasError('required')) {
    <p class="v3-field-error fs-body-xs-alt">{{ 'translations.global.input.error.enterPassword' | transloco }}</p>
  }
  @if (signupDetailsFormGroup.controls.password.touched && signupDetailsFormGroup.controls.password.hasError('pattern')) {
    <p class="v3-field-error fs-body-xs-alt">{{ 'translations.global.input.error.enterValidPassword' | transloco }}</p>
  }

  <ptl-sg-checkbox
    class="v3-terms-checkbox"
    formControlName="termsOfService"
    [ariaLabel]="'translations.global.ariaLabels.termsOfServiceCheckbox' | transloco"
  >
    <span class="v3-terms-text fs-body-s" [innerHTML]="'translations.public.description.termsOfService' | transloco"></span>
  </ptl-sg-checkbox>

  <re-captcha #captchaElem [siteKey]="reCaptchaSiteKeyV2" (resolved)="proceedIfValid($event)" size="invisible"></re-captcha>

  @if (accountCreationError$ | async; as accountCreationError) {
    <p class="v3-field-error fs-body-xs-alt">{{ accountCreationError }}</p>
  }

  @if (invitationId || organizationData?.allowRegistrations) {
    @let isCreatingAccount = accountCreationInProgress$ | async;
    <div class="v3-actions">
      <ptl-sg-button type="flat" color="primary" class="v3-submit" [disabled]="isCreatingAccount" (clicked)="onSignUpClick()">
        @if (!isCreatingAccount) {
          <span>{{ 'translations.public.button.signUp' | transloco }}</span>
        } @else {
          <span class="v3-submit-loading">
            <span>{{ 'translations.public.button.creatingAccount' | transloco }}</span>
            <mat-spinner [diameter]="18"></mat-spinner>
          </span>
        }
      </ptl-sg-button>
    </div>
  }

  @if (organizationData?.authType === 'SOCIAL' && !invitationId) {
    <div class="v3-divider">
      <span class="overline" [attr.aria-label]="'translations.global.ariaLabels.orSignUpWith' | transloco">{{
        'translations.public.label.signUpWith' | transloco
      }}</span>
    </div>
    <div class="v3-social-buttons">
      <button class="v3-social-button v3-social-button--linkedin" title="Sign up with LinkedIn" (click)="signInUsingLinkedIn()">
        <img alt="" src="assets/social/linkedin-white.svg" width="291" height="79" />
      </button>
      <button class="v3-social-button v3-social-button--google" title="Sign up with Google" (click)="signInUsingGoogle()">
        <img alt="" src="assets/social/google-colored.svg" width="265" height="87" />
      </button>
    </div>
  }
</div>
